// REL_1.1.14 F-262 / S-983 — Marketing layout for deedo-marketing public site.
// Ported from deedo-frontend MarketingLayout.tsx with simplifications:
//   - No auth-store / no RoleNames / no dashboard routing (marketing site is unauthenticated only)
//   - "Log In" → app.deedo.ai/login + "List Property" → app.deedo.ai/signup, cross-domain per Q13 user-direct
//     (REL_1.1.15D GH#47: "Log In" previously mis-pointed at /signup — a returning user landed on the signup page)
//     (no cross-domain cookie sniffing; simple href)
//   - Footer Privacy/ToS links cross-domain to app.deedo.ai/privacy-policy + /terms-of-use
//     (auth-bound legal-text canonical per F-262 spec §1.1 user-direct decision)
//   - Drops ToastContainer (marketing site uses inline form feedback; no store-driven toasts)
// Layout structure preserved: sticky header + 4-link nav + auth CTAs + main + 4-col footer.
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import DeedoLogo from "./DeedoLogo";
import MarketingFooter from "./MarketingFooter";

// Cross-domain app SPA origin for "Log In" / "List Property" CTAs.
// Per-env config in .env.development (localhost:3100) / .env.staging / .env.production.
const APP_BASE_URL: string = import.meta.env.VITE_APP_BASE_URL || "https://app.deedo.ai";

// REL_1.1.15 S-1101 v2.8 — `/us/` prefix dropped (hardcode-US descope).
const PUBLIC_NAV = [
  { to: "/for-buyers", label: "For Buyers" },
  { to: "/for-agents", label: "For Agents" },
  { to: "/pricing",    label: "Pricing" },
  { to: "/about",      label: "About" },
  { to: "/contact",    label: "Contact" },
];

export default function MarketingLayout() {
  // hide nav highlights on universal `/` landing so country-selector doesn't look "active"
  const { pathname } = useLocation();
  const onUniversalLanding = pathname === "/";

  return (
    <div className="flex min-h-screen flex-col bg-white">
      {/* Top nav — white background, dark text, sticky */}
      <header className="sticky top-0 z-30 border-b border-gray-200 bg-white">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 lg:px-8">
          {/* Brand */}
          <Link to="/" className="flex items-center gap-2" aria-label="Deedo home">
            <DeedoLogo size={36} />
            <span className="text-2xl font-bold tracking-tight text-[var(--brand-primary)]">Deedo</span>
          </Link>

          {/* Public nav — centred on desktop; hidden on universal landing */}
          {!onUniversalLanding && (
            <nav className="hidden gap-8 md:flex" aria-label="Marketing navigation">
              {PUBLIC_NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `text-sm font-medium transition-colors ${
                      isActive ? "text-[var(--brand-secondary)]" : "text-gray-700 hover:text-[var(--brand-primary)]"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          )}

          {/* Auth CTAs (cross-domain to app SPA per F-262 Q13 user-direct) */}
          <div className="flex items-center gap-3">
            <a
              href={`${APP_BASE_URL}/login`}
              className="hidden text-sm font-medium text-gray-700 hover:text-[var(--brand-primary)] sm:inline-block"
              aria-label="Log in"
            >
              Log In
            </a>
            <span className="hidden h-6 w-px bg-gray-200 sm:inline-block" aria-hidden="true" />
            <a
              href={`${APP_BASE_URL}/signup`}
              className="rounded-lg bg-[var(--brand-primary)] px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90"
              aria-label="List your property"
            >
              List Property
            </a>
          </div>
        </div>
      </header>

      {/* Page content rendered through route Outlet */}
      <main className="flex-1">
        <Outlet />
      </main>

      <MarketingFooter appBaseUrl={APP_BASE_URL} />
    </div>
  );
}
